import React from "react";

// Reusable row for a paid feature with a quota:
//  - bool toggle (the entitlement)
//  - when ON, exposes an "Unlimited" checkbox + a numeric quota field
//    (number disabled when Unlimited is on)
// Styling mirrors the MFA / Trial Experience rows in VendorOrganizationForm
// so the look is identical across the Settings tab.
const EntitlementWithQuotaRow = ({
  id,
  label,
  description,
  enabled,
  quota,
  unlimited,
  used,
  quotaLabel,
  unlimitedLabel,
  isEditMode,
  isSaving,
  onEnabledChange,
  onQuotaChange,
  onUnlimitedChange,
}) => {
  const interactive = isEditMode && !isSaving;
  const usedNumber = Number.isFinite(Number(used)) ? Number(used) : 0;
  const quotaNumber = Number.isFinite(Number(quota)) ? Number(quota) : 0;

  return (
    <div className="space-y-2">
      <label
        className={`flex gap-3 ${interactive ? "cursor-pointer" : "cursor-default pointer-events-none"}`}
      >
        <div className="flex h-6 shrink-0 items-center">
          <div className="group grid size-4 grid-cols-1">
            <input
              id={id}
              type="checkbox"
              checked={!!enabled}
              readOnly={!interactive}
              onChange={() => interactive && onEnabledChange(!enabled)}
              className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            />
            <svg
              viewBox="0 0 16 16"
              fill="white"
              className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100"
              aria-hidden
            >
              <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <span className="text-sm/6 font-medium text-gray-900">{label}</span>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{description}</p>
          ) : null}
        </div>
      </label>

      {enabled ? (
        <div className="ml-7 space-y-2">
          <label
            className={`flex gap-3 ${interactive ? "cursor-pointer" : "cursor-default pointer-events-none"}`}
          >
            <div className="flex h-6 shrink-0 items-center">
              <div className="group grid size-4 grid-cols-1">
                <input
                  id={`${id}-unlimited`}
                  type="checkbox"
                  checked={!!unlimited}
                  readOnly={!interactive}
                  onChange={() => interactive && onUnlimitedChange(!unlimited)}
                  className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                />
                <svg
                  viewBox="0 0 16 16"
                  fill="white"
                  className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100"
                  aria-hidden
                >
                  <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                </svg>
              </div>
            </div>
            <span className="text-sm text-gray-900">{unlimitedLabel}</span>
          </label>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <label htmlFor={`${id}-quota`}>{quotaLabel}:</label>
            <input
              id={`${id}-quota`}
              type="number"
              min={0}
              value={quota ?? ""}
              disabled={!interactive || !!unlimited}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  onQuotaChange(null);
                  return;
                }
                const n = Number(v);
                onQuotaChange(Number.isFinite(n) ? n : null);
              }}
              className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
            />
            {unlimited ? (
              <span className="text-gray-400">—</span>
            ) : (
              <span className="text-gray-500">
                Used: <span className="font-medium text-gray-700">{usedNumber}</span> of {quotaNumber}
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EntitlementWithQuotaRow;
